import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import gsap from 'gsap';
import { selectTheme } from '../../store/themeSlice';
import { selectLanguage } from '../../store/languageSlice';
import { translations } from '../../data/translations';
import { HiMenu, HiX } from 'react-icons/hi';
import LanguageSwitch from '../LanguageSwitch/LanguageSwitch';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import './Header.css';

const Header = () => {
  const theme = useSelector(selectTheme);
  const language = useSelector(selectLanguage);
  const t = translations[language];
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const scope = headerRef.current;
    if (!scope || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        scope.querySelector('.header-logo-slot'),
        { autoAlpha: 0, y: -12 },
        { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out', delay: 0.15 },
      );

      const navItems = scope.querySelectorAll('.desktop-nav > *');
      gsap.fromTo(
        navItems,
        { autoAlpha: 0, y: -10 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.06,
          ease: 'power3.out',
          delay: 0.35,
        },
      );
    }, scope);

    return () => ctx.revert();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false); // Chiudi il menu dopo il click
    }
  };

  const navItems = [
    { id: 'hero', label: t.nav.home },
    { id: 'about', label: t.nav.about },
    { id: 'experience', label: t.nav.experience },
    { id: 'education', label: t.nav.education }
  ];

  // Chiudi il menu quando la finestra viene ridimensionata oltre md
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Previeni lo scroll del body quando il menu è aperto
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <>
      <header 
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50 h-[70px] transition-colors duration-300"
        style={{
          backgroundColor: 'transparent',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between">
          <button
            type="button"
            id="header-logo-slot"
            className="header-logo-slot"
            onClick={() => scrollToSection('hero')}
            aria-label={language === 'it' ? 'Torna all\'inizio' : 'Back to top'}
          />

          {/* Desktop Navigation */}
          <nav className="desktop-nav hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button 
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="header-nav-item px-4 py-2 text-base font-medium"
                style={{
                  color: 'var(--text-secondary)',
                  backgroundColor: 'transparent',
                  transition: 'all 0.3s ease-out',
                  textShadow: 'none',
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = 'var(--hover-accent)';
                  e.target.style.textShadow = 'var(--hover-text-glow)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = 'var(--text-secondary)';
                  e.target.style.textShadow = 'none';
                }}
              >
                {item.label}
              </button>
            ))}
            
            <ThemeToggle />
            <LanguageSwitch />
          </nav>

          {/* Mobile Burger Button */}
          <button
            className="mobile-burger md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            style={{
              color: isMenuOpen ? 'var(--hover-accent)' : 'var(--text-secondary)',
              backgroundColor: 'transparent',
              transition: 'all 0.3s ease-out',
            }}
          >
            {isMenuOpen ? (
              <HiX 
                size={28} 
                style={{ 
                  transition: 'transform 0.3s ease-out',
                  filter: 'drop-shadow(0 0 4px rgba(254, 91, 0, 0.5))'
                }} 
              />
            ) : (
              <HiMenu size={28} style={{ transition: 'transform 0.3s ease-out' }} />
            )}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <nav
        className={`fixed top-[70px] right-0 bottom-0 z-40 w-80 md:hidden transition-transform duration-300 ease-out ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          backgroundColor: 'var(--background-color)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div className="flex flex-col h-full pt-8">
          {navItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="px-6 py-4 text-left text-lg font-medium"
              style={{
                color: 'var(--text-secondary)',
                backgroundColor: 'transparent',
                transition: 'all 0.3s ease-out',
                textShadow: 'none',
                animation: isMenuOpen 
                  ? `slideInRight 0.3s ease-out ${index * 0.05}s both`
                  : 'none',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--hover-accent)';
                e.target.style.textShadow = 'var(--hover-text-glow)';
                e.target.style.backgroundColor = 'var(--surface-color)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--text-secondary)';
                e.target.style.textShadow = 'none';
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              {item.label}
            </button>
          ))}
          
          <div className="flex items-center justify-center gap-6 mt-auto mb-8 px-6">
            <ThemeToggle />
            <LanguageSwitch />
          </div>
        </div>
      </nav>

      {/* CSS Animations and Responsive Styles */}
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        /* Force hide desktop nav on mobile */
        @media (max-width: 767px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-burger {
            display: block !important;
          }
        }
        
        /* Force show desktop nav on tablet+ */
        @media (min-width: 768px) {
          .desktop-nav {
            display: flex !important;
          }
          .mobile-burger {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default Header;